import type { LiftXDatabase } from '../db';
import type Dexie from 'dexie';

/**
 * The client-side registry of synced tables — "LiftX Sync Blueprint" §3.
 * Mirrors `functions/api/_lib/syncTables.ts`'s `SYNC_TABLE_KEYS` exactly (same
 * 17 keys, same order doesn't matter since both sides key by name) — the two
 * files can't share code across the app/functions build boundary, so this is
 * the one place the client's half of that contract lives.
 *
 * Deliberately absent, matching the server: `settings`/`meta` (device
 * preferences), `backupRecords` (this device's own backup history), and
 * `personalRecords` (a rebuildable cache — the sync engine calls
 * `personalRecordsRepository.rebuildAll()` after a pull instead of syncing it
 * directly, per blueprint §22).
 */
export const SYNC_TABLE_KEYS = [
  'exercises',
  'exerciseUserState',
  'userMedia',
  'plans',
  'planDays',
  'planDaySchedules',
  'planBlocks',
  'planExercises',
  'planSets',
  'progressionRules',
  'sessions',
  'sessionBlocks',
  'sessionExercises',
  'sessionSets',
  'cardioSegments',
  'measurementTypes',
  'bodyMeasurements',
  'historyEditAudit',
] as const;

export type SyncTableKey = (typeof SYNC_TABLE_KEYS)[number];

/** Every table whose rows can affect `personalRecords` — a pull touching any of these triggers one `rebuildAll()` at the end of the cycle. */
export const PR_AFFECTING_TABLES: readonly SyncTableKey[] = [
  'exercises',
  'sessions',
  'sessionExercises',
  'sessionSets',
  'cardioSegments',
];

export function syncTable(database: LiftXDatabase, key: SyncTableKey): Dexie.Table<{ id: string }, string> {
  switch (key) {
    case 'exercises':
      return database.exercises as unknown as Dexie.Table<{ id: string }, string>;
    case 'exerciseUserState':
      return database.exerciseUserState as unknown as Dexie.Table<{ id: string }, string>;
    case 'userMedia':
      return database.userMedia as unknown as Dexie.Table<{ id: string }, string>;
    case 'plans':
      return database.plans as unknown as Dexie.Table<{ id: string }, string>;
    case 'planDays':
      return database.planDays as unknown as Dexie.Table<{ id: string }, string>;
    case 'planDaySchedules':
      return database.planDaySchedules as unknown as Dexie.Table<{ id: string }, string>;
    case 'planBlocks':
      return database.planBlocks as unknown as Dexie.Table<{ id: string }, string>;
    case 'planExercises':
      return database.planExercises as unknown as Dexie.Table<{ id: string }, string>;
    case 'planSets':
      return database.planSets as unknown as Dexie.Table<{ id: string }, string>;
    case 'progressionRules':
      return database.progressionRules as unknown as Dexie.Table<{ id: string }, string>;
    case 'sessions':
      return database.sessions as unknown as Dexie.Table<{ id: string }, string>;
    case 'sessionBlocks':
      return database.sessionBlocks as unknown as Dexie.Table<{ id: string }, string>;
    case 'sessionExercises':
      return database.sessionExercises as unknown as Dexie.Table<{ id: string }, string>;
    case 'sessionSets':
      return database.sessionSets as unknown as Dexie.Table<{ id: string }, string>;
    case 'cardioSegments':
      return database.cardioSegments as unknown as Dexie.Table<{ id: string }, string>;
    case 'measurementTypes':
      return database.measurementTypes as unknown as Dexie.Table<{ id: string }, string>;
    case 'bodyMeasurements':
      return database.bodyMeasurements as unknown as Dexie.Table<{ id: string }, string>;
    case 'historyEditAudit':
      return database.historyEditAudit as unknown as Dexie.Table<{ id: string }, string>;
  }
}
